import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { Config } from '../config';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';

@Injectable({
    providedIn: 'root'
})
export class HttpService {

    constructor(private http: HttpClient, private authService: AuthService) {}

    private isPublicEndpoint(url: string): boolean {
        // Verifica si la URL es pública comprobando si contiene '/api/public/'
        // o endpoints de cuenta que no requieren autenticación
        return url.includes('/api/public/')
            || url.includes('api/account')
            || url.includes('assets/')
            || url.includes('maps.googleapis.com');
    }

    private handleError(error: any) {
        console.log('service-error: ', error);
        return throwError(error);
    }

    // Método para crear los headers con token asíncrono
    private buildHeaders(token: string): { headers: HttpHeaders } {
        let httpHeaders = new HttpHeaders({
            'Content-Type': 'application/json',
        });

        if (token) {
            httpHeaders = httpHeaders.append('Authorization', `Bearer ${token}`);
        }

        return { headers: httpHeaders };
    }

    private buildHeadersFile(token: string, data: any): { headers: HttpHeaders } {
        console.log("service-buildHeadersFile", data);
        let httpHeaders = new HttpHeaders();

        if (token) {
            httpHeaders = httpHeaders.append('Authorization', `Bearer ${token}`);
        }

        // Solo agrega Content-Type si NO es FormData (lo maneja automáticamente el navegador)
        if (!(data instanceof FormData)) {
            httpHeaders = httpHeaders.append('Content-Type', 'application/json');
        }

        return { headers: httpHeaders };
    }

    // Método GET que utiliza el token adecuado
    public get(url: string): Observable<any> {
        console.log("service-get",url);
        if (this.isPublicEndpoint(url)) {
            return this.http.get<any>(Config.URL + url).pipe(
                catchError(this.handleError)
            );
        }

        return this.authService.getAuthToken().pipe(
            switchMap(token =>
                this.http.get<any>(Config.URL + url, this.buildHeaders(token)).pipe(
                    catchError(this.handleError)
                )
            )
        );
    }

    // Método POST que utiliza el token adecuado
    public post(url: string, data: any): Observable<any> {
        console.log("service-post",url, data);
        if (this.isPublicEndpoint(url)) {
            return this.http.post<any>(Config.URL + url, data).pipe(
                catchError(this.handleError)
            );
        }

        return this.authService.getAuthToken().pipe(
            switchMap(token =>
                this.http.post<any>(Config.URL + url, data, this.buildHeaders(token)).pipe(
                    catchError(this.handleError)
                )
            )
        );
    }

    /**
     * Método para solicitudes POST
     *
     * @param url --> Ruta de invocación
     * @param data --> Datos para la invocación
     */
    public postFile(url: string, data: any): Observable<any> {
        console.log("service-postFile",url, data);
        if (this.isPublicEndpoint(url)) {
            return this.http.post<any>(Config.URL + url, data, this.buildHeadersFile('', data)).pipe(
                catchError(this.handleError)
            );
        }

        return this.authService.getAuthToken().pipe(
            switchMap(token =>
                this.http.post<any>(Config.URL + url, data, this.buildHeadersFile(token, data)).pipe(
                    catchError(this.handleError)
                )
            )
        );
    }

    // Método PUT que utiliza el token adecuado
    public put(url: string, data: any): Observable<any> {
        console.log("service-put",url, data);
        if (this.isPublicEndpoint(url)) {
            return this.http.put<any>(Config.URL + url, data).pipe(
                catchError(this.handleError)
            );
        }

        return this.authService.getAuthToken().pipe(
            switchMap(token =>
                this.http.put<any>(Config.URL + url, data, this.buildHeaders(token)).pipe(
                    catchError(this.handleError)
                )
            )
        );
    }

    // Método PATCH que utiliza el token adecuado
    public patch(url: string, data: any): Observable<any> {
        console.log("service-patch", url, data);
        if (this.isPublicEndpoint(url)) {
            return this.http.patch<any>(Config.URL + url, data).pipe(
                catchError(this.handleError)
            );
        }

        return this.authService.getAuthToken().pipe(
            switchMap(token =>
                this.http.patch<any>(Config.URL + url, data, this.buildHeaders(token)).pipe(
                    catchError(this.handleError)
                )
            )
        );
    }

    // Método GET para obtener reportes (PDF, blobs, etc.) que utiliza el token adecuado
    public getReport(url: string): Observable<Blob> {
        console.log("service-getReport", url);
        if (this.isPublicEndpoint(url)) {
            return this.http.get(Config.URL + url, {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
                responseType: 'blob'
            }).pipe(catchError(this.handleError));
        }

        return this.authService.getAuthToken().pipe(
            switchMap(token =>
                this.http.get(Config.URL + url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    }),
                    responseType: 'blob',
                }).pipe(catchError(this.handleError))
            )
        );
    }
    // Método DELETE que utiliza el token adecuado
    public delete(url: string): Observable<any> {
        console.log("service-delete", url);
        console.log(url);
        if (this.isPublicEndpoint(url)) {
            return this.http.delete<any>(Config.URL + url).pipe(
                catchError(this.handleError)
            );
        }

        return this.authService.getAuthToken().pipe(
            switchMap(token =>
                this.http.delete<any>(Config.URL + url, this.buildHeaders(token)).pipe(
                    catchError(this.handleError)
                )
            )
        );
    }

}
